import { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../api";
import { useAuth } from "../AuthContext";
import CopyButton from "../components/CopyButton";
import SiteJobs from "../components/SiteJobs";

// Status → badge color. CMS sites are "sample" until first published, then
// "published"; "live" is the public/indexed state, "draft" handled defensively.
const STATUS_COLORS = {
  published: "#1f7a3d",
  live: "#1f7a3d",
  draft: "#8a6d1f",
  sample: "#555f6b",
};

// Readable input styling (the dashboard theme otherwise renders these very faint).
const INPUT_STYLE = {
  background: "#fff", color: "#111", border: "1px solid #c4ccd6",
  borderRadius: 4, padding: "5px 8px", fontSize: 13,
};

// 137.4 -> "2m 17s"; under a minute -> "17s".
function formatSeconds(s) {
  const total = Math.round(s || 0);
  const m = Math.floor(total / 60);
  const sec = total % 60;
  return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
}

export default function AthleteSites({ goHome }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all"); // all | live | hidden
  const [showArchived, setShowArchived] = useState(false);  // archived sites are out of sight by default
  const [statsBySlug, setStatsBySlug] = useState({});


  // per-site action state
  const [busy, setBusy] = useState("");          // slug currently mutating
  const [rowErr, setRowErr] = useState({});       // slug -> error string
  const [domainDraft, setDomainDraft] = useState({}); // slug -> edited domain
  const [provReport, setProvReport] = useState({}); // slug -> provisioning report

  // The server hides archived sites unless asked; asking is what the toggle does.
  const load = useCallback((withArchived = showArchived) => {
    return api.get("/sites", { params: withArchived ? { include_archived: true } : {} }).then(({ data }) => {
      // GET /api/sites returns a bare array (unlike /website-tests' {items})
      setSites(Array.isArray(data) ? data : (data.items || []));
      setLoading(false);
    }).catch(() => {
      setError(true);
      setLoading(false);
    });
  }, [showArchived]);

  useEffect(() => { load(); }, [load]);

  // Best-effort visitor/engagement/leads glance per card. Never blocks the gallery.
  useEffect(() => {
    api.get("/analytics/rollup").then(({ data }) => {
      const map = {};
      (data.per_site || []).forEach((p) => { map[p.slug] = p; });
      setStatsBySlug(map);
    }).catch(() => {});
  }, []);

  const origin = typeof window !== "undefined" ? window.location.origin : "";

  // Archived sites never mix into the main grid or its counts — they get their
  // own section below, and only when the toggle is on.
  const activeSites = sites.filter((s) => !s.archived);
  const archivedSites = sites.filter((s) => s.archived);

  // Status filter + live-first ordering for the grid below. "live" is the
  // public/indexed state; everything else (sample/draft/published) is "hidden".
  const liveCount = activeSites.filter((s) => s.status === "live").length;
  const visibleSites = activeSites
    .filter((s) =>
      statusFilter === "live" ? s.status === "live"
      : statusFilter === "hidden" ? s.status !== "live"
      : true)
    .sort((a, b) => (b.status === "live") - (a.status === "live"));


  async function setStatus(slug, status) {
    setBusy(slug); setRowErr((m) => ({ ...m, [slug]: "" }));
    try {
      await api.patch(`/sites/${slug}/status`, { status });
      await load();
    } catch (err) {
      setRowErr((m) => ({ ...m, [slug]: formatApiErrorDetail(err?.response?.data?.detail) || "Failed." }));
    } finally {
      setBusy("");
    }
  }

  async function setArchived(slug, archived) {
    setBusy(slug); setRowErr((m) => ({ ...m, [slug]: "" }));
    try {
      await api.patch(`/sites/${slug}/archive`, { archived });
      // Archiving from the main grid makes the card disappear; keep the list in
      // whatever mode the toggle is in rather than forcing archived into view.
      await load();
    } catch (err) {
      setRowErr((m) => ({ ...m, [slug]: formatApiErrorDetail(err?.response?.data?.detail) || "Failed." }));
    } finally {
      setBusy("");
    }
  }

  async function provision(slug) {
    const site = sites.find((s) => s.slug === slug);
    const domain = (domainDraft[slug] ?? (site?.custom_domain) ?? "").trim();
    if (!domain) { setRowErr((m) => ({ ...m, [slug]: "Enter the domain first, then Provision." })); return; }
    setBusy(slug); setRowErr((m) => ({ ...m, [slug]: "" })); setProvReport((m) => ({ ...m, [slug]: null }));
    try {
      const { data } = await api.post(`/sites/${slug}/provision`, { domain });
      setProvReport((m) => ({ ...m, [slug]: data }));
      await load();
    } catch (err) {
      setRowErr((m) => ({ ...m, [slug]: formatApiErrorDetail(err?.response?.data?.detail) || "Provision failed." }));
    } finally {
      setBusy("");
    }
  }

  async function saveDomain(slug) {
    const value = (domainDraft[slug] ?? "").trim();
    setBusy(slug); setRowErr((m) => ({ ...m, [slug]: "" }));
    try {
      await api.patch(`/sites/${slug}/custom-domain`, { custom_domain: value || null });
      setDomainDraft((m) => { const n = { ...m }; delete n[slug]; return n; });
      await load();
    } catch (err) {
      setRowErr((m) => ({ ...m, [slug]: formatApiErrorDetail(err?.response?.data?.detail) || "Failed." }));
    } finally {
      setBusy("");
    }
  }

  return (
    <div className="view" data-testid="athlete-sites-view">
      <div className="crumb">
        <a onClick={goHome} data-testid="as-back-to-home">← Home</a>
        {" / "}
        Athlete Sites
      </div>

      <div className="ver-head" style={{ marginTop: 16 }}>
        <div>
          <div className="sec-head" style={{ margin: 0 }}>Athlete Sites</div>
          <div className="sec-desc">
            Live CMS sites — edited point-and-click (open <strong>Edit</strong> to change text,
            photos, and news right on the page). New sites start hidden (noindex);
            {isAdmin ? " only you can publish a site live or bind its domain." : " an admin publishes them live."}
          </div>
        </div>
      </div>

      {isAdmin && (
        <div
          data-testid="as-golive-help"
          style={{
            margin: "12px 0",
            padding: "10px 12px",
            border: "1px solid var(--line,#2a2e35)",
            borderRadius: 8,
          }}
        >
          <div className="sec-desc" style={{ margin: 0 }}>
            <strong>Taking a site live:</strong>{" "}
            ① enter the domain → <strong>Save domain</strong> → ② <strong>Provision domain</strong>{" "}
            (auto-wires DNS, www &amp; TLS) → ③ wait for the certificate to issue →{" "}
            ④ <strong>Publish Live</strong>. A brand-new domain must be added to Cloudflare
            (nameservers pointed there) before provisioning.
          </div>
        </div>
      )}

      <SiteJobs onSiteLive={load} />

      {loading ? (
        <div className="disc-empty">Loading…</div>
      ) : error ? (
        <div className="disc-empty" data-testid="as-error">Couldn&apos;t load athlete sites. Refresh to try again.</div>
      ) : sites.length === 0 && showArchived ? (
        <div className="disc-empty">No athlete sites yet — use <strong>Request new site</strong> above.</div>
      ) : (
        <>
          <div className="as-filter" data-testid="as-filter" style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", margin: "12px 0" }}>
            <span className="sec-desc" style={{ margin: 0, marginRight: 4 }}>Show:</span>
            {[
              { key: "all", label: `All (${activeSites.length})` },
              { key: "live", label: `Live (${liveCount})` },
              { key: "hidden", label: `Hidden (${activeSites.length - liveCount})` },
            ].map((opt) => (
              <button
                key={opt.key}
                className={`btn btn-sm${statusFilter === opt.key ? " btn-primary" : ""}`}
                onClick={() => setStatusFilter(opt.key)}
                aria-pressed={statusFilter === opt.key}
                data-testid={`as-filter-${opt.key}`}
              >
                {opt.label}
              </button>
            ))}
            <button
              className={`btn btn-sm${showArchived ? " btn-primary" : ""}`}
              onClick={() => setShowArchived((v) => !v)}
              aria-pressed={showArchived}
              title="Sites you archived are out of the way by default. This brings them back into view."
              data-testid="as-toggle-archived"
              style={{ marginLeft: "auto" }}
            >
              {showArchived ? "Hide archived" : "Show archived"}
            </button>
          </div>
          {visibleSites.length === 0 ? (
            <div className="disc-empty" data-testid="as-filter-empty">
              {sites.length === 0 ? "No athlete sites here — everything may be archived, or use Request new site above."
                : statusFilter === "live" ? "No sites are live yet."
                : "No hidden sites."}
            </div>
          ) : (
          <div className="wt-grid">
            {visibleSites.map((s) => {
            const pageUrl = `/api/sites/${s.slug}/page`;
            const editUrl = `${pageUrl}?edit=1`;
            const shareUrl = `${origin}${pageUrl}`;
            const badge = STATUS_COLORS[s.status] || "#555f6b";
            const isLive = s.status === "live";
            const domainVal = domainDraft[s.slug] ?? (s.custom_domain || "");
            // A live site with a custom domain: "View" opens the real public site,
            // not the internal dashboards.* preview URL. Others keep the preview.
            const viewUrl = isLive && s.custom_domain ? `https://${s.custom_domain}` : pageUrl;
            return (
              <div className="wt-card" key={s.slug} data-testid={`as-card-${s.slug}`}>
                <a
                  href={pageUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="wt-thumb"
                  style={s.thumb ? { backgroundImage: `url('${encodeURI(s.thumb)}')` } : undefined}
                  data-testid={`as-thumb-${s.slug}`}
                >
                  {!s.thumb && <span className="wt-thumb-fallback">{s.athlete_name || s.slug}</span>}
                </a>
                <div className="wt-card-body">
                  <div className="wt-card-title">
                    {s.athlete_name || s.slug}
                    {s.status && (
                      <span
                        style={{
                          marginLeft: 8, fontSize: 11, fontWeight: 600, color: "#fff",
                          background: badge, borderRadius: 4, padding: "1px 6px",
                          verticalAlign: "middle", textTransform: "capitalize",
                        }}
                        data-testid={`as-status-${s.slug}`}
                      >
                        {s.status}
                      </span>
                    )}
                    {s.has_commercial_photos && (
                      <span
                        style={{
                          marginLeft: 8, fontSize: 11, fontWeight: 600, color: "#fff",
                          background: "#a15c1b", borderRadius: 4, padding: "1px 6px",
                          verticalAlign: "middle",
                        }}
                        title="Some photos are commercial/editorial (demo use) and should be swapped for licensed or rights-clear images before this site goes to production."
                        data-testid={`as-commercial-${s.slug}`}
                      >
                        Commercial photos
                      </span>
                    )}
                  </div>
                  {s.updated_at && (
                    <div className="wt-card-date">Updated {String(s.updated_at).slice(0, 10)}</div>
                  )}
                  {Object.keys(statsBySlug[s.slug]?.ga4 || {}).length > 0 && (
                    <div className="wt-card-stats" data-testid={`as-stats-${s.slug}`}>
                      {statsBySlug[s.slug].ga4.active_users} visitors · {formatSeconds(statsBySlug[s.slug].ga4.avg_engagement_seconds)} avg time · {statsBySlug[s.slug].bookings_total} leads
                    </div>
                  )}
                  <div className="wt-card-actions">
                    <a
                      href={editUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-sm btn-primary"
                      data-testid={`as-edit-${s.slug}`}
                    >
                      Edit
                    </a>
                    <a
                      href={viewUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-sm"
                      data-testid={`as-open-${s.slug}`}
                    >
                      View
                    </a>
                    <CopyButton url={shareUrl} testid={`as-copy-${s.slug}`} />
                    {/* Archive is a view-only tidy-up: both roles, always reversible,
                        and it changes nothing the public can see. */}
                    <button
                      className="btn btn-sm"
                      disabled={busy === s.slug}
                      onClick={() => setArchived(s.slug, true)}
                      title="Hide this site from these lists. Nothing changes for visitors; you can bring it back with Show archived."
                      data-testid={`as-archive-${s.slug}`}
                    >
                      Archive
                    </button>
                  </div>
                  {rowErr[s.slug] && !isAdmin && (
                    <div style={{ color: "#c0392b", fontSize: 12, marginTop: 6 }} data-testid={`as-row-err-${s.slug}`}>
                      {rowErr[s.slug]}
                    </div>
                  )}

                  {/* Go-live + domain are the technical legal gate: admin (Marco) only. */}
                  {isAdmin && (
                    <div className="as-admin" style={{ marginTop: 10, borderTop: "1px solid var(--line,#2a2e35)", paddingTop: 10 }}>
                      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                        {isLive ? (
                          <button
                            className="btn btn-sm"
                            disabled={busy === s.slug}
                            onClick={() => { if (window.confirm(`Take ${s.athlete_name || s.slug} OFFLINE (back to hidden)?`)) setStatus(s.slug, "sample"); }}
                            data-testid={`as-unpublish-${s.slug}`}
                          >
                            Take offline
                          </button>
                        ) : (
                          <button
                            className="btn btn-sm btn-primary"
                            disabled={busy === s.slug}
                            onClick={() => { if (window.confirm(`Publish ${s.athlete_name || s.slug} LIVE and public (indexable by Google)?`)) setStatus(s.slug, "live"); }}
                            data-testid={`as-golive-${s.slug}`}
                          >
                            Publish Live
                          </button>
                        )}
                      </div>
                      <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 8 }}>
                        <input
                          type="text"
                          value={domainVal}
                          placeholder="theirname.com"
                          onChange={(e) => setDomainDraft((m) => ({ ...m, [s.slug]: e.target.value }))}
                          data-testid={`as-domain-input-${s.slug}`}
                          style={{ ...INPUT_STYLE, flex: 1, minWidth: 0 }}
                        />
                        <button
                          className="btn btn-sm"
                          disabled={busy === s.slug || domainVal === (s.custom_domain || "")}
                          onClick={() => saveDomain(s.slug)}
                          data-testid={`as-domain-save-${s.slug}`}
                        >
                          Save domain
                        </button>
                      </div>
                      <div style={{ marginTop: 8 }}>
                        <button
                          className="btn btn-sm"
                          disabled={busy === s.slug}
                          onClick={() => provision(s.slug)}
                          title="Wire up the domain: Railway + Cloudflare DNS + Turnstile + email. Does not publish."
                          data-testid={`as-provision-${s.slug}`}
                        >
                          {busy === s.slug ? "Provisioning…" : "Provision domain"}
                        </button>
                      </div>
                      {rowErr[s.slug] && (
                        <div style={{ color: "#c0392b", fontSize: 12, marginTop: 6 }} data-testid={`as-row-err-${s.slug}`}>
                          {rowErr[s.slug]}
                        </div>
                      )}
                      {provReport[s.slug] && (
                        <div style={{ marginTop: 8, fontSize: 12 }} data-testid={`as-prov-report-${s.slug}`}>
                          {provReport[s.slug].missing_credentials ? (
                            <div style={{ color: "#c0392b" }}>
                              Missing on Railway: {provReport[s.slug].missing_credentials.join(", ")}
                            </div>
                          ) : (
                            <>
                              <div style={{ fontWeight: 600, color: provReport[s.slug].ok ? "#1f7a3d" : "#c0392b" }}>
                                {provReport[s.slug].ok ? "Provisioned ✓" : "Some steps failed — details below"}
                              </div>
                              <ul style={{ margin: "4px 0 0", paddingLeft: 16 }}>
                                {(provReport[s.slug].steps || []).map((st, i) => (
                                  <li key={i} style={{ color: st.ok ? "#1f7a3d" : "#c0392b" }}>
                                    {st.ok ? "✓" : "✗"} {st.step}
                                    {!st.ok && st.detail && st.detail.error && (
                                      <span style={{ color: "#889" }}> — {JSON.stringify(st.detail.error).slice(0, 240)}</span>
                                    )}
                                  </li>
                                ))}
                              </ul>
                              {provReport[s.slug].note && (
                                <div style={{ color: "#889", marginTop: 4 }}>{provReport[s.slug].note}</div>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          </div>
          )}

          {showArchived && (
            <div data-testid="as-archived-section" style={{ marginTop: 28 }}>
              <div className="sec-head" style={{ marginBottom: 4 }}>
                Archived ({archivedSites.length})
              </div>
              <div className="sec-desc" style={{ marginTop: 0 }}>
                Out of the way, not deleted or changed. Each of these still serves publicly
                exactly as it did before it was archived.
              </div>
              {archivedSites.length === 0 ? (
                <div className="disc-empty" data-testid="as-archived-empty">Nothing archived yet.</div>
              ) : (
                <div className="wt-grid" style={{ marginTop: 12 }}>
                  {archivedSites.map((s) => (
                    <div className="wt-card" key={s.slug} data-testid={`as-archived-card-${s.slug}`} style={{ opacity: 0.62 }}>
                      <a
                        href={`/api/sites/${s.slug}/page`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="wt-thumb"
                        style={s.thumb ? { backgroundImage: `url('${encodeURI(s.thumb)}')`, filter: "grayscale(1)" } : { filter: "grayscale(1)" }}
                      >
                        {!s.thumb && <span className="wt-thumb-fallback">{s.athlete_name || s.slug}</span>}
                      </a>
                      <div className="wt-card-body">
                        <div className="wt-card-title">
                          {s.athlete_name || s.slug}
                          <span
                            style={{
                              marginLeft: 8, fontSize: 11, fontWeight: 600, color: "#fff",
                              background: "#555f6b", borderRadius: 4, padding: "1px 6px",
                              verticalAlign: "middle",
                            }}
                            data-testid={`as-archived-badge-${s.slug}`}
                          >
                            Archived
                          </span>
                        </div>
                        <div className="wt-card-date">
                          {s.archived_by ? `By ${s.archived_by}` : "Archived"}
                          {s.archived_at ? ` · ${String(s.archived_at).slice(0, 10)}` : ""}
                          {s.status === "live" ? " · still live publicly" : ""}
                        </div>
                        <div className="wt-card-actions">
                          <button
                            className="btn btn-sm btn-primary"
                            disabled={busy === s.slug}
                            onClick={() => setArchived(s.slug, false)}
                            data-testid={`as-unarchive-${s.slug}`}
                          >
                            Unarchive
                          </button>
                          <a
                            href={`/api/sites/${s.slug}/page?edit=1`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-sm"
                            data-testid={`as-archived-edit-${s.slug}`}
                          >
                            Edit
                          </a>
                        </div>
                        {rowErr[s.slug] && (
                          <div style={{ color: "#c0392b", fontSize: 12, marginTop: 6 }}>{rowErr[s.slug]}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
